/**
 * Tabellen-Ansicht Tab für Schritt 3
 * Zeigt Portfolio-Positionen mit editierbaren Verkauf %-Spalten
 */

import React, { useState, useMemo, useRef } from 'react';
import {
  Table,
  Header,
  SpaceBetween,
  Box,
  Container,
  ColumnLayout,
  Input,
  FormField,
  Button,
  Alert,
  Select,
  PieChart,
  Slider,
  Checkbox
} from '@cloudscape-design/components';

import {
  PortfolioPosition,
  EntnahmeSimulation,
  CacheData,
  SteuerParameter,
  OptimizationResults,
  OptimizationSellInstruction,
  OptimizationPositionAnalysis,
  OptimizationLotAnalysisRow
} from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { PortfolioService } from '@/services/portfolioService';
import { SPARERFREIBETRAEGE, calculateEffectiveCapitalGainsTax } from '@/utils/constants';
import { validateSparerPauschbetrag, devValidate } from '@/utils/taxValidation';
import {
  berechneNettoKapitalsteuerNachVerrechnung,
  portfolioPositionKey
} from '@/utils/capitalGainsAggregation';

// Steuerbehandlung Optionen
const TAX_TREATMENT_OPTIONS = [
  { label: 'keine Teilfreistellung', value: 'keine Teilfreistellung' },
  { label: 'Teilfreistellung 15%', value: 'Teilfreistellung 15%' },
  { label: 'Teilfreistellung 30%', value: 'Teilfreistellung 30%' },
  { label: 'Steuerfrei wenn Haltefrist >1 Jahr', value: 'Steuerfrei Haltefrist >1 Jahr' }
];

interface TabellenAnsichtTabProps {
  portfolioPositionen: PortfolioPosition[];
  onPositionUpdate: (positionen: PortfolioPosition[]) => void;
  entnahmeSimulation: EntnahmeSimulation | null;
  steuerParameter: SteuerParameter | null;
  onSimulationUpdate: (simulation: EntnahmeSimulation) => void;
  sparerPauschbetragEnabled?: boolean;
  onSparerPauschbetragChange?: (enabled: boolean) => void;
  zielEntnahmebetrag?: string;
  onZielEntnahmebetragChange?: (value: string) => void;
  portfolioPercentageRaw?: number;
  onPortfolioPercentageRawChange?: (value: number) => void;
}

/**
 * Komponente für die Tabellen-Ansicht mit Portfolio-Positionen
 */
const TabellenAnsichtTab: React.FC<TabellenAnsichtTabProps> = ({
  portfolioPositionen,
  onPositionUpdate,
  entnahmeSimulation,
  steuerParameter,
  onSimulationUpdate,
  sparerPauschbetragEnabled: externalSparerPauschbetragEnabled,
  onSparerPauschbetragChange,
  zielEntnahmebetrag: externalZielEntnahmebetrag,
  onZielEntnahmebetragChange,
  portfolioPercentageRaw: externalPortfolioPercentageRaw,
  onPortfolioPercentageRawChange
}) => {
  // Use external state if provided, otherwise fall back to local state for backwards compatibility
  const [localZielEntnahmebetrag, setLocalZielEntnahmebetrag] = useState<string>('');
  const [localPortfolioPercentageRaw, setLocalPortfolioPercentageRaw] = useState<number>(0.0);
  
  const zielEntnahmebetrag = externalZielEntnahmebetrag ?? localZielEntnahmebetrag;
  const portfolioPercentageRaw = externalPortfolioPercentageRaw ?? localPortfolioPercentageRaw;
  
  const setZielEntnahmebetrag = (value: string) => {
    if (onZielEntnahmebetragChange) {
      onZielEntnahmebetragChange(value);
    } else {
      setLocalZielEntnahmebetrag(value);
    }
  };
  
  const setPortfolioPercentageRaw = (value: number) => {
    if (onPortfolioPercentageRawChange) {
      onPortfolioPercentageRawChange(value);
    } else {
      setLocalPortfolioPercentageRaw(value);
    }
  };
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  
  // Sparer-Pauschbetrag State (use external state if provided, otherwise local state)
  const [localSparerPauschbetragEnabled, setLocalSparerPauschbetragEnabled] = useState(false);
  const sparerPauschbetragEnabled = externalSparerPauschbetragEnabled ?? localSparerPauschbetragEnabled;
  
  const setSparerPauschbetragEnabled = (enabled: boolean) => {
    if (onSparerPauschbetragChange) {
      onSparerPauschbetragChange(enabled);
    } else {
      setLocalSparerPauschbetragEnabled(enabled);
    }
  };

  // Helper function to aggregate positions by ISIN for KPIs and charts
  const aggregatePositionsByISIN = useMemo(() => {
    const aggregatedMap = new Map<string, {
      identifier: string;
      name: string;
      totalValue: number;
      totalBuyingValue: number;
      sellPercentage: number; // Average weighted by totalValue
      totalSellValue: number;
      hasAnyPosition: boolean; // True if any portfolio position will remain after sale
    }>();

    portfolioPositionen.forEach(pos => {
      const isin = pos.identifier;
      const existing = aggregatedMap.get(isin);
      const effectiveSellPct = pos.exclude ? 0 : pos.sellPercentage;
      
      // Calculate buying value from lots
      const buyingValue = pos.purchaseLots.reduce((sum, lot) => sum + (lot.quantity * lot.price), 0);
      const sellValue = pos.totalValue * (effectiveSellPct / 100);
      
      if (existing) {
        // Aggregate values
        const oldTotalValue = existing.totalValue;
        const newTotalValue = existing.totalValue + pos.totalValue;
        
        // Weighted average sell percentage
        const weightedSellPercentage = newTotalValue > 0 
          ? ((existing.sellPercentage * oldTotalValue) + (effectiveSellPct * pos.totalValue)) / newTotalValue
          : 0;
          
        existing.totalValue = newTotalValue;
        existing.totalBuyingValue += buyingValue;
        existing.sellPercentage = weightedSellPercentage;
        existing.totalSellValue += sellValue;
        existing.hasAnyPosition = existing.hasAnyPosition || (effectiveSellPct !== 100);
      } else {
        // First position for this ISIN
        aggregatedMap.set(isin, {
          identifier: isin,
          name: pos.name,
          totalValue: pos.totalValue,
          totalBuyingValue: buyingValue,
          sellPercentage: effectiveSellPct,
          totalSellValue: sellValue,
          hasAnyPosition: effectiveSellPct !== 100
        });
      }
    });

    return Array.from(aggregatedMap.values());
  }, [portfolioPositionen]);
  
  // Sparer-Pauschbetrag Betrag aus Berechnungsparametern ermitteln
  const sparerPauschbetragBetrag = steuerParameter 
    ? SPARERFREIBETRAEGE[steuerParameter.familienstand]
    : SPARERFREIBETRAEGE.alleinstehend;
  
  // Always ensure portfolio percentage is step-aligned
  const portfolioPercentage = useMemo(() => {
    return Math.round(portfolioPercentageRaw * 10) / 10;
  }, [portfolioPercentageRaw]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Utility function to ensure slider value is aligned with 0.1 step
  const alignToStep = (value: number): number => {
    return Math.round(value * 10) / 10;
  };

  // Safe setter for portfolio percentage that ensures step alignment
  /*
  const setPortfolioPercentageSafe = (value: number) => {
    const clampedValue = Math.max(0, Math.min(10, value)); // Clamp to 0-10 range
    setPortfolioPercentageRaw(clampedValue); // useMemo will handle alignment
  };
  */

  // Sorting state
  const [sortingColumn, setSortingColumn] = useState<string>('name');
  const [sortingDescending, setSortingDescending] = useState<boolean>(false);
  const [cacheImportError, setCacheImportError] = useState<string | null>(null);
  const [cacheImportWarning, setCacheImportWarning] = useState<string | null>(null);

  // Tax optimization results state
  const [lastOptimizationResults, setLastOptimizationResults] = useState<OptimizationResults | null>(null);
  const [showOptimizationDetails, setShowOptimizationDetails] = useState<boolean>(false);

  /**
   * Handler für Tabellen-Sortierung
   */
  const handleSortingChange = (detail: { sortingColumn: { sortingField?: string }; isDescending?: boolean }) => {
    setSortingColumn(detail.sortingColumn.sortingField || 'name');
    setSortingDescending(detail.isDescending || false);
  };

  // Portfolio-Gesamtwert und Performance berechnen (MOVED UP for sorting logic and useEffect dependencies)
  const portfolioStatistiken = useMemo(() => {
    const gesamtCurrentValue = portfolioPositionen.reduce((sum, pos) => sum + pos.totalValue, 0);
    
    const gesamtBuyingValue = portfolioPositionen.reduce((sum, pos) => {
      const buyingValue = pos.purchaseLots.reduce((lotSum, lot) => {
        return lotSum + (lot.quantity * lot.price);
      }, 0);
      return sum + buyingValue;
    }, 0);
    
    const performance = gesamtBuyingValue > 0 ? ((gesamtCurrentValue - gesamtBuyingValue) / gesamtBuyingValue) * 100 : 0;
    const absoluteGain = gesamtCurrentValue - gesamtBuyingValue;
    
    return {
      gesamtCurrentValue,
      gesamtBuyingValue,
      performance,
      absoluteGain,
      anzahlPositionen: aggregatePositionsByISIN.length // Use aggregated count by ISIN
    };
  }, [portfolioPositionen, aggregatePositionsByISIN]);

  // Ensure initial value is properly aligned (run once on mount)
  React.useEffect(() => {
    const currentValue = portfolioPercentageRaw;
    const alignedValue = alignToStep(currentValue);
    if (Math.abs(currentValue - alignedValue) > 0.001) {
      setPortfolioPercentageRaw(alignedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: align only on mount
  }, []);

  // Mark initial load as complete when portfolio data is available
  React.useEffect(() => {
    if (isInitialLoad && portfolioStatistiken.gesamtCurrentValue > 0) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, portfolioStatistiken.gesamtCurrentValue]);

  // Sync portfolio percentage when amount changes manually (reverse calculation)
  React.useEffect(() => {
    if (!isInitialLoad && portfolioStatistiken.gesamtCurrentValue > 0) { // Only sync after initial load
      const numericAmount = parseFloat(zielEntnahmebetrag) || 0;
      if (numericAmount > 0) {
        const calculatedPercentage = (numericAmount / portfolioStatistiken.gesamtCurrentValue) * 100;
        
        // Only update if the calculated percentage is different and within valid range (0-10%)
        // Use larger threshold to prevent rapid back-and-forth updates
        if (Math.abs(calculatedPercentage - portfolioPercentageRaw) > 0.1 && calculatedPercentage >= 0 && calculatedPercentage <= 10) {
          setPortfolioPercentageRaw(alignToStep(calculatedPercentage));
        }
      } else if (numericAmount === 0) {
        // If amount is cleared, reset percentage to 0
        setPortfolioPercentageRaw(0);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setPortfolioPercentageRaw is stable; portfolioPercentageRaw omitted to avoid sync loops
  }, [zielEntnahmebetrag, portfolioStatistiken.gesamtCurrentValue, isInitialLoad]);
  
  // Sortierte Portfolio-Positionen
  const sortedPortfolioPositionen = useMemo(() => {
    if (!sortingColumn) return portfolioPositionen;
    
    const sorted = [...portfolioPositionen].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      // Handle calculated fields
      if (sortingColumn === 'portfolioAnteil') {
        aValue = portfolioStatistiken.gesamtCurrentValue > 0 
          ? (a.totalValue / portfolioStatistiken.gesamtCurrentValue) * 100 
          : 0;
        bValue = portfolioStatistiken.gesamtCurrentValue > 0 
          ? (b.totalValue / portfolioStatistiken.gesamtCurrentValue) * 100 
          : 0;
      } else if (sortingColumn === 'portfolioAnteilNachVerkauf') {
        // Calculate portfolio percentage after sales for sorting
        const totalPortfolioValueAfterSales = portfolioStatistiken.gesamtCurrentValue - 
          portfolioPositionen.reduce((sum, pos) => {
            if (pos.exclude) return sum;
            return sum + (pos.totalValue * (pos.sellPercentage / 100));
          }, 0);
        
        const aSaleProceeds = a.exclude ? 0 : a.totalValue * (a.sellPercentage / 100);
        const aItemValueAfterSale = a.totalValue - aSaleProceeds;
        aValue = totalPortfolioValueAfterSales > 0 ? (aItemValueAfterSale / totalPortfolioValueAfterSales) * 100 : 0;
        
        const bSaleProceeds = b.exclude ? 0 : b.totalValue * (b.sellPercentage / 100);
        const bItemValueAfterSale = b.totalValue - bSaleProceeds;
        bValue = totalPortfolioValueAfterSales > 0 ? (bItemValueAfterSale / totalPortfolioValueAfterSales) * 100 : 0;
      } else if (sortingColumn === 'gewinnVerlust') {
        aValue = a.totalValue - (a.averageBuyPrice * a.quantity);
        bValue = b.totalValue - (b.averageBuyPrice * b.quantity);
      } else if (sortingColumn === 'saleProceeds') {
        aValue = a.exclude ? 0 : a.totalValue * (a.sellPercentage / 100);
        bValue = b.exclude ? 0 : b.totalValue * (b.sellPercentage / 100);
      } else if (sortingColumn === 'taxableProfit') {
        aValue = a.exclude ? 0 : PortfolioService.calculateFIFOTaxableProfit(a, a.sellPercentage);
        bValue = b.exclude ? 0 : PortfolioService.calculateFIFOTaxableProfit(b, b.sellPercentage);
      } else {
        // Handle regular fields
        aValue = a[sortingColumn as keyof PortfolioPosition];
        bValue = b[sortingColumn as keyof PortfolioPosition];
      }
      
      // Convert to numbers if possible for proper numeric sorting
      const aNum = typeof aValue === 'string' ? parseFloat(aValue) : aValue;
      const bNum = typeof bValue === 'string' ? parseFloat(bValue) : bValue;
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortingDescending ? bNum - aNum : aNum - bNum;
      }
      
      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortingDescending) {
        return bStr.localeCompare(aStr);
      } else {
        return aStr.localeCompare(bStr);
      }
    });
    
    return sorted;
  }, [portfolioPositionen, sortingColumn, sortingDescending, portfolioStatistiken]);

  /**
   * Handler für Verkauf %-Eingabe
   */
  const handleVerkaufPercentageChange = (positionKey: string, value: string) => {
    const numericValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
    
    const updatedPositions = portfolioPositionen.map(pos =>
      portfolioPositionKey(pos) === positionKey
        ? { ...pos, sellPercentage: numericValue, purchaseLots: pos.purchaseLots || [] }
        : pos
    );
    
    onPositionUpdate(updatedPositions);
    
    // Simulation aktualisieren
    updateSimulation(updatedPositions, parseFloat(zielEntnahmebetrag) || 0);
  };

  /**
   * Handler für Steuerbehandlung-Auswahl
   */
  const handleTaxTreatmentChange = (positionKey: string, value: string) => {
    const updatedPositions = portfolioPositionen.map(pos =>
      portfolioPositionKey(pos) === positionKey
        ? { ...pos, taxTreatment: value || 'keine Teilfreistellung', purchaseLots: pos.purchaseLots || [] }
        : pos
    );
    
    onPositionUpdate(updatedPositions);
  };

  /**
   * Handler für Exclude-Checkbox-Änderung
   */
  const handleExcludeChange = (positionKey: string, checked: boolean) => {
    const updatedPositions = portfolioPositionen.map(pos =>
      portfolioPositionKey(pos) === positionKey
        ? { ...pos, exclude: checked, purchaseLots: pos.purchaseLots || [] }
        : pos
    );
    
    onPositionUpdate(updatedPositions);
    
    // Update simulation with current values
    updateSimulation(updatedPositions, parseFloat(zielEntnahmebetrag) || 0);
  };

  /**
   * Aktualisiert die Entnahme-Simulation
   */
  const updateSimulation = (positionen: PortfolioPosition[], zielBetrag: number) => {
    const tatsaechlicherVerkaufserloes = positionen.reduce((sum, pos) => {
      if (pos.exclude) return sum;
      const verkaufswert = pos.totalValue * (pos.sellPercentage / 100);
      return sum + verkaufswert;
    }, 0);

    // Calculate goal achievement percentage with proper bounds checking
    // Formula: Zielerreichung = (Brutto-Verkaufserlös / Gewünschter Verkaufserlös in €) * 100%
    // Example: If target is €10,000 and actual sales is €3,000, then achievement is 30%
    let zielerreichung = 0;
    if (zielBetrag > 0 && tatsaechlicherVerkaufserloes >= 0) {
      zielerreichung = (tatsaechlicherVerkaufserloes / zielBetrag) * 100;
      // Round to 1 decimal place to avoid floating point precision issues
      zielerreichung = Math.round(zielerreichung * 10) / 10;
    }

    const simulation: EntnahmeSimulation = {
      zielEntnahmebetrag: zielBetrag,
      tatsaechlicherVerkaufserloes,
      zielerreichung
    };

    onSimulationUpdate(simulation);
  };

  // Steuerberechnung: Verlustverrechnung über Positionen, dann Pauschbetrag, dann eine KapSt-Rechnung
  const steuerStatistiken = useMemo(() => {
    const emptyMap = new Map<string, number>();
    const positionsForTaxCalculation = [...portfolioPositionen].sort((a, b) => a.name.localeCompare(b.name));
    const rows = positionsForTaxCalculation
      .filter((pos) => !pos.exclude && pos.sellPercentage > 0)
      .map((pos) => ({
        positionKey: portfolioPositionKey(pos),
        gewinn: PortfolioService.calculateFIFOTaxableProfit(pos, pos.sellPercentage)
      }));

    if (rows.length === 0) {
      return {
        gesamtSteuerpflichtigerGewinn: 0,
        gesamtSteuer: 0,
        gesamtVerwendeterPauschbetrag: 0,
        verfuegbarerPauschbetrag: sparerPauschbetragEnabled ? sparerPauschbetragBetrag : 0,
        steuerpflichtigerGewinnNachPauschProPosition: emptyMap
      };
    }

    const net = berechneNettoKapitalsteuerNachVerrechnung(
      rows,
      steuerParameter,
      sparerPauschbetragEnabled,
      sparerPauschbetragBetrag
    );

    if (sparerPauschbetragEnabled && net.verwendeterPauschbetrag > 0) {
      devValidate(
        validateSparerPauschbetrag,
        steuerParameter?.familienstand || 'alleinstehend',
        net.verwendeterPauschbetrag,
        true
      );
    }

    return {
      gesamtSteuerpflichtigerGewinn: net.steuerpflichtigerGewinnNachPausch,
      gesamtSteuer: net.gesamtsteuer,
      gesamtVerwendeterPauschbetrag: net.verwendeterPauschbetrag,
      verfuegbarerPauschbetrag: sparerPauschbetragEnabled
        ? sparerPauschbetragBetrag - net.verwendeterPauschbetrag
        : 0,
      steuerpflichtigerGewinnNachPauschProPosition: net.steuerpflichtigerGewinnNachPauschProPosition
    };
  }, [portfolioPositionen, steuerParameter, sparerPauschbetragEnabled, sparerPauschbetragBetrag]);

  // Pie chart data for current portfolio allocation (Cloudscape format) - aggregated by ISIN
  const currentPortfolioChartData = useMemo(() => {
    if (!aggregatePositionsByISIN || aggregatePositionsByISIN.length === 0 || portfolioStatistiken.gesamtCurrentValue <= 0) {
      return [];
    }
    
    return aggregatePositionsByISIN
      .filter(agg => agg && agg.totalValue > 0)
      .map((agg, index) => ({
        title: "",
        value: (agg.totalValue / portfolioStatistiken.gesamtCurrentValue) * 100,
        datum: agg.totalValue,
        originalName: agg.name || `Asset ${index + 1}`
      }))
      .filter(item => item.value > 0 && !isNaN(item.value))
      .sort((a, b) => b.value - a.value);
  }, [aggregatePositionsByISIN, portfolioStatistiken.gesamtCurrentValue]);

  // Pie chart data for portfolio allocation after sales (Cloudscape format) - aggregated by ISIN
  const afterSalePortfolioChartData = useMemo(() => {
    if (!aggregatePositionsByISIN || aggregatePositionsByISIN.length === 0 || portfolioStatistiken.gesamtCurrentValue <= 0) {
      return [];
    }

    // Calculate total portfolio value after all sales (use aggregated data)
    const totalPortfolioValueAfterSales = aggregatePositionsByISIN.reduce((sum, agg) => {
      if (!agg || typeof agg.totalValue !== 'number' || typeof agg.sellPercentage !== 'number') {
        return sum;
      }
      const valueAfterSale = agg.totalValue - agg.totalSellValue;
      return sum + valueAfterSale;
    }, 0);

    if (totalPortfolioValueAfterSales <= 0) {
      return [];
    }

    return aggregatePositionsByISIN
      .filter(agg => agg && agg.totalValue > 0)
      .map((agg, index) => {
        const valueAfterSale = agg.totalValue - agg.totalSellValue;
        const percentageAfterSale = valueAfterSale > 0 ? (valueAfterSale / totalPortfolioValueAfterSales) * 100 : 0;
        
        return {
          title: "",
          value: percentageAfterSale,
          datum: valueAfterSale,
          originalName: agg.name || `Asset ${index + 1}`
        };
      })
      .filter(item => item.value > 0 && !isNaN(item.value) && !isNaN(item.datum))
      .sort((a, b) => b.value - a.value);
  }, [aggregatePositionsByISIN, portfolioStatistiken.gesamtCurrentValue]);

  // Portfolio statistics after planned sales
  const portfolioStatistikenNachVerkauf = useMemo(() => {
    if (!portfolioPositionen || portfolioPositionen.length === 0) {
      return {
        gesamtCurrentValueNachVerkauf: 0,
        gesamtBuyingValueNachVerkauf: 0,
        performanceNachVerkauf: 0,
        absoluteGainNachVerkauf: 0,
        anzahlPositionenNachVerkauf: 0
      };
    }

    let gesamtCurrentValueNachVerkauf = 0;
    let gesamtBuyingValueNachVerkauf = 0;

    // Use raw portfolio positions for value calculations (since they're accurate)
    portfolioPositionen.forEach(pos => {
      const effectiveSellPct = pos.exclude ? 0 : pos.sellPercentage;
      const saleProceeds = pos.totalValue * (effectiveSellPct / 100);
      const valueAfterSale = pos.totalValue - saleProceeds;
      
      if (valueAfterSale > 0) {
        gesamtCurrentValueNachVerkauf += valueAfterSale;
        
        const buyingValueAfterSale = PortfolioService.calculateRemainingBuyingValueAfterPartialSale(
          pos,
          effectiveSellPct
        );
        gesamtBuyingValueNachVerkauf += buyingValueAfterSale;
      }
    });

    // Count remaining positions by ISIN (aggregated)
    const anzahlPositionenNachVerkauf = aggregatePositionsByISIN.filter(agg => agg.hasAnyPosition).length;

    const absoluteGainNachVerkauf = gesamtCurrentValueNachVerkauf - gesamtBuyingValueNachVerkauf;
    const performanceNachVerkauf = gesamtBuyingValueNachVerkauf > 0 
      ? (absoluteGainNachVerkauf / gesamtBuyingValueNachVerkauf) * 100 
      : 0;

    return {
      gesamtCurrentValueNachVerkauf,
      gesamtBuyingValueNachVerkauf,
      performanceNachVerkauf,
      absoluteGainNachVerkauf,
      anzahlPositionenNachVerkauf
    };
  }, [portfolioPositionen, aggregatePositionsByISIN]);

  /**
   * Handler für Ziel-Entnahmebetrag-Änderung
   */
  const handleZielEntnahmebetragChange = (value: string) => {
    setZielEntnahmebetrag(value);
    const numericValue = parseFloat(value) || 0;
    updateSimulation(portfolioPositionen, numericValue);
  };

  /**
   * Cache Import Handler
   */
  const handleCacheImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setCacheImportError(null);
    setCacheImportWarning(null);

    // Check file type
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        
        // Check if file content is empty
        if (!content || content.trim() === '') {
          throw new Error('Die Datei ist leer');
        }

        // Parse JSON
        const cacheData: CacheData = JSON.parse(content);
        
        if (!cacheData.taxTreatmentsByPositionKey && !cacheData.taxTreatments) {
          throw new Error(
            'Ungültige Cache-Datenstruktur. Erwartet: taxTreatmentsByPositionKey und/oder taxTreatments (Legacy).'
          );
        }

        const updatedPositions = portfolioPositionen.map((pos) => {
          const taxTreatment =
            cacheData.taxTreatmentsByPositionKey?.[portfolioPositionKey(pos)] ??
            cacheData.taxTreatments?.[pos.name];

          const updates: Partial<PortfolioPosition> = {
            purchaseLots: pos.purchaseLots || []
          };

          if (taxTreatment) {
            updates.taxTreatment = taxTreatment || 'keine Teilfreistellung';
          }

          return { ...pos, ...updates };
        });

        const nameCounts = portfolioPositionen.reduce<Record<string, number>>((acc, pos) => {
          acc[pos.name] = (acc[pos.name] || 0) + 1;
          return acc;
        }, {});
        const hasDuplicateNames = Object.values(nameCounts).some((n) => n > 1);
        const legacy = cacheData.taxTreatments;
        const usedLegacyNameLookup = portfolioPositionen.some(
          (pos) =>
            !cacheData.taxTreatmentsByPositionKey?.[portfolioPositionKey(pos)] && !!legacy?.[pos.name]
        );
        if (usedLegacyNameLookup && hasDuplicateNames) {
          setCacheImportWarning(
            'Hinweis: Es wurde ein Legacy-Import nur nach Wertpapiername genutzt. Mehrere Positionen tragen denselben Namen — die Zuordnung kann falsch sein. Bitte Cache mit taxTreatmentsByPositionKey (ISIN|Portfolio) exportieren.'
          );
        } else {
          setCacheImportWarning(null);
        }

        onPositionUpdate(updatedPositions);
        updateSimulation(updatedPositions, parseFloat(zielEntnahmebetrag) || 0);
        setCacheImportError(null);

      } catch (error) {
        setCacheImportError(
          error instanceof Error ? error.message : 'Import fehlgeschlagen'
        );
      }
    };

    reader.onerror = () => {
      setCacheImportError('Datei konnte nicht gelesen werden');
    };

    reader.readAsText(file);
    
    // Reset input
    event.target.value = '';
  };

  /**
   * Cache Export Handler
   */
  const handleCacheExport = () => {
    try {
      const taxTreatmentsByPositionKey: Record<string, string> = {};
      const cacheData: CacheData = {
        taxTreatmentsByPositionKey,
        timestamp: Date.now(),
        version: '2.0'
      };

      portfolioPositionen.forEach((pos) => {
        if (pos.taxTreatment) {
          taxTreatmentsByPositionKey[portfolioPositionKey(pos)] = pos.taxTreatment;
        }
      });

      // Create and download JSON file
      const jsonString = JSON.stringify(cacheData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Portfolio-Wizard-Daten-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch {
      // Fehler beim Exportieren der Cache-Datei
    }
  };

  /**
   * Funktionen für verschiedene Aktionen
   */
  const handleCacheLöschen = () => {
    const clearedPositions = portfolioPositionen.map(pos => ({
      ...pos,
      taxTreatment: 'keine Teilfreistellung',
      purchaseLots: pos.purchaseLots || []
    }));
    setCacheImportWarning(null);
    onPositionUpdate(clearedPositions);
    updateSimulation(clearedPositions, parseFloat(zielEntnahmebetrag) || 0);
  };



  /**
   * Handler für Verkauf % leeren - Setzt alle Verkaufsanteile auf 0%
   */
  const handleVerkaufLeeren = () => {
    const clearedPositions = portfolioPositionen.map(pos => ({
      ...pos,
      sellPercentage: 0,
      purchaseLots: pos.purchaseLots || []
    }));
    
    // Clear optimization results when clearing sales
    setLastOptimizationResults(null);
    
    onPositionUpdate(clearedPositions);
    updateSimulation(clearedPositions, parseFloat(zielEntnahmebetrag) || 0);
  };

  /**
   * Handler für Symmetrischer Verkauf - Verkauft von allen Positionen proportional
   */
  const handleSymmetrischerVerkauf = () => {
    const targetAmount = parseFloat(zielEntnahmebetrag) || 0;
    
    // Clear optimization results when switching to symmetrical selling
    setLastOptimizationResults(null);
    
    // First, reset all selling percentages to 0%
    const resetPositions = portfolioPositionen.map(pos => ({
      ...pos,
      sellPercentage: 0,
      purchaseLots: pos.purchaseLots || []
    }));
    
    // Filter out excluded positions for calculation
    const includedPositions = resetPositions.filter(pos => !pos.exclude);
    const totalIncludedValue = includedPositions.reduce((sum, pos) => sum + pos.totalValue, 0);
    
    if (targetAmount <= 0) {
      alert('Bitte geben Sie einen gültigen Ziel-Entnahmebetrag ein (größer als 0).');
      return;
    }
    
    if (totalIncludedValue <= 0) {
      alert('Keine Positionen mit Wert > 0 verfügbar für symmetrischen Verkauf. Überprüfen Sie, ob Positionen ausgeschlossen sind.');
      return;
    }
    
    // Berechne den symmetrischen Verkaufsanteil (gleicher Prozentsatz für alle nicht ausgeschlossenen Positionen)
    const symmetricSellPercentage = Math.min(100, Math.round((targetAmount / totalIncludedValue) * 100 * 100) / 100);
    
    // Wende den gleichen Verkaufsanteil nur auf nicht ausgeschlossene Positionen an
    const updatedPositions = resetPositions.map(pos => ({
      ...pos,
      sellPercentage: pos.exclude ? 0 : symmetricSellPercentage,
      purchaseLots: pos.purchaseLots || []
    }));
    
    onPositionUpdate(updatedPositions);
    updateSimulation(updatedPositions, targetAmount);
  };

  /**
   * Handler für Simulation einer möglichen Verkaufsreihenfolge - Simuliert Verkauf mit FIFO-basierter Berechnung
   */
  const handleSteuerOptimiererVerkauf = () => {
    const targetAmount = parseFloat(zielEntnahmebetrag) || 0;
    const totalPortfolioValue = portfolioStatistiken.gesamtCurrentValue;
    
    if (targetAmount <= 0) {
      alert('Bitte geben Sie einen gültigen Ziel-Entnahmebetrag ein (größer als 0).');
      return;
    }
    
    if (totalPortfolioValue <= 0) {
      alert('Portfolio-Wert ist 0. Bitte laden Sie Portfolio-Daten oder aktuelle Preise.');
      return;
    }

    if (targetAmount > totalPortfolioValue) {
      alert(`Der Ziel-Entnahmebetrag (${formatCurrency(targetAmount)}) übersteigt den verfügbaren Portfolio-Wert (${formatCurrency(totalPortfolioValue)}). Bitte reduzieren Sie den Betrag.`);
      return;
    }

    // Reset all selling percentages to 0%
    const resetPositions = portfolioPositionen.map(pos => ({
      ...pos,
      sellPercentage: 0,
      purchaseLots: pos.purchaseLots || []
    }));

    const optimizationResult = PortfolioService.calculateFIFOTaxOptimization(
      resetPositions,
      targetAmount,
      steuerParameter,
      sparerPauschbetragEnabled,
      sparerPauschbetragBetrag
    );

    const updatedPositions = optimizationResult.optimizedPositions;
    // const totalProceeds = optimizationResult.totalProceeds;
    // const totalTaxAmount = optimizationResult.totalTaxAmount;

    // FIFO calculation completed - results stored for UI display

    // Ensure excluded positions have 0% sell percentage
    const finalUpdatedPositions = updatedPositions.map(pos => ({
      ...pos,
      sellPercentage: pos.exclude ? 0 : pos.sellPercentage,
      purchaseLots: pos.purchaseLots || []
    }));

    // Generate detailed lot analysis for sold positions
    const analyzeLots = (position: PortfolioPosition, steuerParams: SteuerParameter | null) => {
      if (!position.purchaseLots || position.purchaseLots.length === 0) {
        return [];
      }

      const currentDate = new Date();
      
      return position.purchaseLots.map((lot, index) => {
        const purchaseDate = new Date(lot.date);
        const holdingPeriodYears = (currentDate.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
        const currentValue = lot.quantity * position.currentPrice;
        const profit = currentValue - (lot.quantity * lot.price);
        
        // Determine if this lot is tax-free
        const isTaxFree = position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && holdingPeriodYears >= 1;
        const isLoss = profit < 0;
        
        // Calculate effective tax treatment
        let effectiveTaxTreatment = position.taxTreatment || 'keine Teilfreistellung';
        if (position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && holdingPeriodYears < 1) {
          effectiveTaxTreatment = 'keine Teilfreistellung';
        }
        
        // Calculate taxable profit
        let taxableProfit = profit;
        if (isTaxFree) {
          taxableProfit = 0;
        } else if (effectiveTaxTreatment === 'Teilfreistellung 15%') {
          taxableProfit = profit * 0.85;
        } else if (effectiveTaxTreatment === 'Teilfreistellung 30%') {
          taxableProfit = profit * 0.70;
        }
        
        const taxCalc = calculateEffectiveCapitalGainsTax(
          Math.max(0, taxableProfit),
          steuerParams?.kirchensteuer
        );
        const taxAmount = taxCalc.gesamtsteuer;
        const taxPerEuro = currentValue > 0 ? taxAmount / currentValue : 0;
        
        // Calculate priority (lower = better)
        let priority = 10000; // Base priority
        if (isLoss) {
          priority = Math.abs(profit); // Losses first (bigger losses = lower number = higher priority)
        } else if (isTaxFree) {
          priority = 5000 + profit; // Tax-free gains
        } else {
          priority = 10000 + taxableProfit; // Regular gains by taxable profit
        }
        
        return {
          lotIndex: index,
          portfolio: lot.portfolio || position.portfolio || 'Unknown',
          quantity: lot.quantity,
          purchasePrice: lot.price,
          currentValue: currentValue,
          holdingPeriodYears: holdingPeriodYears,
          profit: profit,
          taxableProfit: taxableProfit,
          taxAmount: taxAmount,
          taxPerEuro: taxPerEuro,
          isTaxFree: isTaxFree,
          isLoss: isLoss,
          effectiveTaxTreatment: effectiveTaxTreatment,
          priority: priority
        };
      });
    };

    // Generate detailed lot analysis for ALL positions (not just sold ones)
    const detailedAnalysis: OptimizationPositionAnalysis[] = resetPositions
      .filter(pos => !pos.exclude && pos.totalValue > 0)
      .map(position => {
        const lots = analyzeLots(position, steuerParameter);
        const pk = portfolioPositionKey(position);
        return {
          positionKey: pk,
          positionName: position.name,
          identifier: position.identifier,
          taxTreatment: position.taxTreatment || 'keine Teilfreistellung',
          lots,
          isSold:
            (finalUpdatedPositions.find((p) => portfolioPositionKey(p) === pk)?.sellPercentage ?? 0) > 0
        };
      });

    // Use sell instructions from optimization result
    const sellInstructions = optimizationResult.sellInstructions;
    
    setLastOptimizationResults({
      targetAmount,
      achievedAmount: optimizationResult.totalProceeds,
      totalTaxAmount: optimizationResult.totalTaxAmount,
      totalTaxableProfit: optimizationResult.totalTaxableProfit,
      totalFifoTaxableProfitBeforeAllowance: optimizationResult.totalFifoTaxableProfitBeforeAllowance,
      totalSteuerpflichtigerGewinnNachPausch: optimizationResult.totalSteuerpflichtigerGewinnNachPausch,
      usedSparerPauschbetrag: optimizationResult.usedSparerPauschbetrag,
      taxEfficiency: optimizationResult.taxEfficiency,
      sellInstructions: sellInstructions,
      detailedAnalysis: detailedAnalysis,
      timestamp: new Date().toLocaleString('de-DE'),
      strategy: optimizationResult.strategy
    });

    // Sell instructions prepared for UI display

    onPositionUpdate(updatedPositions);
    updateSimulation(updatedPositions, targetAmount);
  };

  // Tabellen-Spaltendefinition
  const columnDefinitions = [
    {
      id: 'name',
      header: 'Wertpapier',
      cell: (item: PortfolioPosition) => {
        const truncatedName = item.name.length > 60 ? `${item.name.substring(0, 60)}...` : item.name;
        if (!item.logo) {
          return (
            <span title={item.name}>
              {truncatedName}
            </span>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img
              src={item.logo}
              alt=""
              width={24}
              height={24}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span title={item.name}>{truncatedName}</span>
          </div>
        );
      },
      sortingField: 'name',
      isRowHeader: true,
      width: 300,
      minWidth: 200
    },
    {
      id: 'identifier',
      header: 'ISIN',
      cell: (item: PortfolioPosition) => item.identifier,
      sortingField: 'identifier',
      width: 120,
      minWidth: 100
    },
    {
      id: 'portfolio',
      header: 'Portfolio',
      cell: (item: PortfolioPosition) => item.portfolio || 'Unknown',
      sortingField: 'portfolio',
      width: 120,
      minWidth: 100
    },
    {
      id: 'quantity',
      header: 'Anteile',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'right' }}>
          {item.quantity.toLocaleString('de-DE')}
        </div>
      ),
      sortingField: 'quantity',
      width: 120,
      minWidth: 100
    },
    {
      id: 'averageBuyPrice',
      header: 'Ø Kaufpreis',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(item.averageBuyPrice, item.currency)}
        </div>
      ),
      sortingField: 'averageBuyPrice',
      width: 130,
      minWidth: 110
    },
    {
      id: 'currentPrice',
      header: 'Aktueller Kurs',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(item.currentPrice, item.currency)}
        </div>
      ),
      sortingField: 'currentPrice',
      width: 130,
      minWidth: 110
    },
    {
      id: 'taxTreatment',
      header: 'Steuerbehandlung',
      cell: (item: PortfolioPosition) => (
        <Select
          selectedOption={TAX_TREATMENT_OPTIONS.find(option => option.value === (item.taxTreatment || 'keine Teilfreistellung')) || null}
          onChange={({ detail }) => handleTaxTreatmentChange(portfolioPositionKey(item), detail.selectedOption?.value ?? '')}
          options={TAX_TREATMENT_OPTIONS}
          placeholder="Auswählen..."
          expandToViewport={true}
          ariaLabel={`Steuerbehandlung für ${item.name}`}
          ariaDescribedby={`tax-help-${item.identifier}`}
          data-testid={`tax-treatment-${item.identifier}`}
        />
      ),
      width: 220,
      minWidth: 200
    },
    {
      id: 'totalValue',
      header: 'Gesamtwert',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(item.totalValue, item.currency)}
        </div>
      ),
      sortingField: 'totalValue',
      width: 140,
      minWidth: 120
    },
    {
      id: 'gewinnVerlust',
      header: 'Gewinn/Verlust (Ø-Kauf)',
      cell: (item: PortfolioPosition) => {
        const gewinn = item.totalValue - (item.averageBuyPrice * item.quantity);
        return (
          <div
            style={{ textAlign: 'right' }}
            title="Nicht FIFO: Differenz aus Gesamtwert und durchschnittlichem Kaufpreis × Menge. Weicht von der Steuerspalte (FIFO-Lots) ab."
          >
            <Box
              variant="span"
              color={
                gewinn > 0 ? "text-status-success" :
                gewinn < 0 ? "text-status-error" :
                "text-body-secondary"
              }
            >
              {gewinn >= 0 ? '+' : ''}{formatCurrency(gewinn, item.currency)}
            </Box>
          </div>
        );
      },
      sortingField: 'gewinnVerlust',
      width: 140,
      minWidth: 120
    },
    {
      id: 'portfolioAnteil',
      header: 'Portfolio-Anteil',
      cell: (item: PortfolioPosition) => {
        const portfolioAnteil = portfolioStatistiken.gesamtCurrentValue > 0 
          ? (item.totalValue / portfolioStatistiken.gesamtCurrentValue) * 100 
          : 0;
        return (
          <div style={{ textAlign: 'right' }}>
            <Box variant="span" color="text-status-info">
              <strong>{portfolioAnteil.toFixed(2)}%</strong>
            </Box>
          </div>
        );
      },
      sortingField: 'portfolioAnteil',
      width: 120,
      minWidth: 100
    },
    {
      id: 'portfolioAnteilNachVerkauf',
      header: 'Portfolio-Anteil nach Verkauf',
      cell: (item: PortfolioPosition) => {
        // Calculate value after sale for this item
        const saleProceeds = item.exclude ? 0 : item.totalValue * (item.sellPercentage / 100);
        const itemValueAfterSale = item.totalValue - saleProceeds;
        
        // Calculate total portfolio value after all sales
        const totalPortfolioValueAfterSales = portfolioStatistiken.gesamtCurrentValue - 
          portfolioPositionen.reduce((sum, pos) => {
            if (pos.exclude) return sum;
            return sum + (pos.totalValue * (pos.sellPercentage / 100));
          }, 0);
        
        // Calculate new portfolio percentage
        const portfolioAnteilNachVerkauf = totalPortfolioValueAfterSales > 0 
          ? (itemValueAfterSale / totalPortfolioValueAfterSales) * 100 
          : 0;
          
        return (
          <div style={{ textAlign: 'right' }}>
            <Box variant="span" color="text-status-success">
              <strong>{portfolioAnteilNachVerkauf.toFixed(2)}%</strong>
            </Box>
          </div>
        );
      },
      sortingField: 'portfolioAnteilNachVerkauf',
      width: 180,
      minWidth: 160
    },
    {
      id: 'exclude',
      header: 'Exclude',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'center' }}>
          <Checkbox
            checked={item.exclude || false}
            onChange={({ detail }) => handleExcludeChange(portfolioPositionKey(item), detail.checked)}
            ariaLabel={`Exclude ${item.name} from calculations`}
          />
        </div>
      ),
      width: 80,
      minWidth: 80
    },
    {
      id: 'sellPercentage',
      header: 'Verkauf %',
      cell: (item: PortfolioPosition) => (
        <div style={{ textAlign: 'right' }}>
        <Input
          value={item.sellPercentage.toString()}
          onChange={({ detail }) => handleVerkaufPercentageChange(portfolioPositionKey(item), detail.value)}
          type="number"
          inputMode="numeric"
          placeholder="0"
            ariaLabel={`Verkaufsanteil für ${item.name} in Prozent`}
            ariaDescribedby={`sell-help-${item.identifier}`}
            step={0.1}
          />
          <div 
            id={`sell-help-${item.identifier}`} 
            style={{ fontSize: '12px', color: '#5f6b7a', marginTop: '2px', textAlign: 'left' }}
            aria-hidden="true"
          >
            Wert zwischen 0 und 100%
          </div>
        </div>
      ),
      width: 120,
      minWidth: 100
    },
    {
      id: 'saleProceeds',
      header: 'Verkaufserlös',
      cell: (item: PortfolioPosition) => {
        const saleProceeds = item.exclude ? 0 : item.totalValue * (item.sellPercentage / 100);
        return (
          <div style={{ textAlign: 'right' }}>
            {formatCurrency(saleProceeds, item.currency)}
          </div>
        );
      },
      sortingField: 'saleProceeds',
      width: 140,
      minWidth: 120
    },
    {
      id: 'taxableProfit',
      header: 'Steuerpflichtiger Gewinn',
      cell: (item: PortfolioPosition) => {
        // Use optimization results if available, otherwise use traditional FIFO calculation
        let steuerpflichtigerGewinn = 0;
        
        if (item.exclude) {
          steuerpflichtigerGewinn = 0;
        } else if (lastOptimizationResults && lastOptimizationResults.sellInstructions) {
          const pk = portfolioPositionKey(item);
          const instruction = lastOptimizationResults.sellInstructions.find(
            (inst: OptimizationSellInstruction) =>
              (inst.positionKey ?? portfolioPositionKey(inst.position)) === pk
          );
          steuerpflichtigerGewinn = instruction
            ? (instruction.effectiveTaxableProfit ?? instruction.totalTaxableProfit ?? 0)
            : 0;
        } else {
          const key = portfolioPositionKey(item);
          const allocated = steuerStatistiken.steuerpflichtigerGewinnNachPauschProPosition.get(key);
          if (allocated !== undefined) {
            steuerpflichtigerGewinn = allocated;
          } else {
            const fifoResult = PortfolioService.calculateFIFOTaxableProfit(item, item.sellPercentage);
            steuerpflichtigerGewinn = typeof fifoResult === 'number' ? fifoResult : 0;
          }
        }
        
        // Ensure we have a valid number
        steuerpflichtigerGewinn = isNaN(steuerpflichtigerGewinn) ? 0 : steuerpflichtigerGewinn;
        
        return (
          <div style={{ textAlign: 'right' }}>
            <Box variant="span" color="text-status-warning">
              <strong>{formatCurrency(steuerpflichtigerGewinn, item.currency)}</strong>
            </Box>
          </div>
        );
      },
      sortingField: 'taxableProfit',
      width: 180,
      minWidth: 150
    }
  ];

  if (portfolioPositionen.length === 0) {
    return (
      <Alert type="info">
        <strong>Keine Portfolio-Positionen verfügbar</strong>
        <br />
        Portfolio-Positionen werden aus den importierten Transaktionsdaten berechnet.
      </Alert>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Aktions-Buttons */}
      <SpaceBetween direction="horizontal" size="m">
        <Button onClick={handleCacheImport} iconName="upload">
          Steuerbehandlung Zuordnung importieren
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button onClick={handleCacheExport} iconName="download">
          Steuerbehandlung Zuordnung exportieren
        </Button>
        <Button onClick={handleCacheLöschen} iconName="remove">
          Steuerbehandlung Zuordnung leeren
        </Button>

      </SpaceBetween>
      {cacheImportError && (
        <Alert
          type="error"
          dismissible
          dismissAriaLabel="Schließen"
          onDismiss={() => setCacheImportError(null)}
        >
          {cacheImportError}
        </Alert>
      )}
      {cacheImportWarning && (
        <Alert
          type="warning"
          dismissible
          dismissAriaLabel="Schließen"
          onDismiss={() => setCacheImportWarning(null)}
        >
          {cacheImportWarning}
        </Alert>
      )}

      {/* Improved layout - stacked sections */}
      <SpaceBetween direction="vertical" size="l">
        {/* Portfolio-Status and Verkaufs-Simulation Side by Side */}
        <Container
          header={
            <Header 
              variant="h3"
              description="Portfolio-Status und Verkaufs-Simulation im Vergleich"
            >
              Portfolio-Übersicht
            </Header>
          }
        >
          <ColumnLayout columns={2}>
            {/* Left Column: Portfolio-Status */}
            <Container
              header={
                <Header variant="h3">
                  Portfolio-Status
                </Header>
              }
            >
              <SpaceBetween direction="vertical" size="l">
                <ColumnLayout columns={2}>
                  {/* Portfolio Status Summary - Left Side */}
                  <div>
                    <SpaceBetween direction="vertical" size="m">
                      <div>
                        <Box variant="h5">Aktueller Portfolio-Wert</Box>
                        <Box variant="awsui-value-large" color="text-status-info">
                          {formatCurrency(portfolioStatistiken.gesamtCurrentValue)}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Marktpreis heute
                        </Box>
                      </div>
                      
                      <div>
                        <Box variant="h5">Einkaufswert</Box>
                        <Box variant="awsui-value-large">
                          {formatCurrency(portfolioStatistiken.gesamtBuyingValue)}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Historische Kosten
                        </Box>
                      </div>
                      
                      <div>
                        <Box variant="h5">Performance</Box>
                        <Box 
                          variant="awsui-value-large" 
                          color={
                            portfolioStatistiken.performance > 0 ? "text-status-success" : 
                            portfolioStatistiken.performance < 0 ? "text-status-error" : 
                            "text-body-secondary"
                          }
                        >
                          {portfolioStatistiken.performance >= 0 ? '+' : ''}{portfolioStatistiken.performance.toFixed(2)}%
                        </Box>
                        <Box 
                          variant="small" 
                          color={
                            portfolioStatistiken.absoluteGain > 0 ? "text-status-success" : 
                            portfolioStatistiken.absoluteGain < 0 ? "text-status-error" : 
                            "text-body-secondary"
                          }
                        >
                          {portfolioStatistiken.absoluteGain >= 0 ? '+' : ''}{formatCurrency(portfolioStatistiken.absoluteGain)} Gewinn/Verlust
                        </Box>
                      </div>

                      <div>
                        <Box variant="h5">Anzahl Positionen</Box>
                        <Box variant="awsui-value-large" color="text-status-info">
                          {portfolioStatistiken.anzahlPositionen}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Portfolio-Positionen
                        </Box>
                        <Box variant="small" color="text-body-secondary" margin={{ top: 'xs' }}>
                          Kreisdiagramme: gleiche ISIN über mehrere Depots zu einem Segment zusammengefasst.
                          Steuer- und FIFO-Berechnung bleiben je ISIN+Depot getrennt.
                        </Box>
                      </div>
                    </SpaceBetween>
                  </div>

                  {/* Current Portfolio Donut Chart - Right Side */}
                  <div>
                    {currentPortfolioChartData && currentPortfolioChartData.length > 0 ? (
                      <PieChart
                        data={currentPortfolioChartData}
                        size="large"
                        detailPopoverContent={(datum, sum) => [
                          { key: 'Asset', value: datum.originalName || 'Unbekannt' },
                          { key: 'Anteil', value: `${((datum.value / sum) * 100).toFixed(2)}%` },
                          { key: 'Wert', value: formatCurrency(datum.datum) }
                        ]}
                        segmentDescription={(datum, sum) =>
                          `${formatCurrency(datum.datum)}, ${((datum.value / sum) * 100).toFixed(1)}%`
                        }
                        ariaDescription="Kreisdiagramm zeigt die aktuelle Portfolio-Verteilung nach Wert."
                        ariaLabel="Portfolio-Verteilung"
                        fitHeight
                        hideDescriptions
                        hideFilter
                        hideLegend
                        hideTitles
                        empty={
                          <Box textAlign="center" color="inherit">
                            <b>Keine Portfolio-Daten</b>
                            <Box variant="p" color="inherit">
                              Es sind keine Portfolio-Daten verfügbar
                            </Box>
                          </Box>
                        }
                        noMatch={
                          <Box textAlign="center" color="inherit">
                            <b>Keine passenden Daten</b>
                            <Box variant="p" color="inherit">
                              Es gibt keine passenden Daten anzuzeigen
                            </Box>
                          </Box>
                        }
                      />
                    ) : (
                      <Box textAlign="center" padding="l">
                        <Box variant="p" color="text-body-secondary">
                          Keine Portfolio-Daten verfügbar
                        </Box>
                      </Box>
                    )}
                    <Box variant="small" color="text-body-secondary" margin={{ top: 'xs' }}>
                      Segmente nach ISIN aggregiert (nicht nach Depot).
                    </Box>
                  </div>
                </ColumnLayout>
              </SpaceBetween>
            </Container>

            {/* Right Column: Verkaufs-Simulation */}
            <Container
              header={
                <Header variant="h3">
                  Verkaufs-Simulation
                </Header>
              }
            >
              <SpaceBetween direction="vertical" size="l">
                <ColumnLayout columns={2}>
                  {/* Portfolio Statistics After Sales */}
                  <div>
                    <SpaceBetween direction="vertical" size="m">
                      <div>
                        <Box variant="h5">Portfolio-Wert nach Verkauf</Box>
                        <Box variant="awsui-value-large" color="text-status-info">
                          {formatCurrency(portfolioStatistikenNachVerkauf.gesamtCurrentValueNachVerkauf)}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Verbleibendes Portfolio
                        </Box>
                      </div>
                        
                        <div>
                          <Box variant="h5">Einkaufswert nach Verkauf</Box>
                          <Box variant="awsui-value-large">
                            {formatCurrency(portfolioStatistikenNachVerkauf.gesamtBuyingValueNachVerkauf)}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Verbleibende Kosten
                          </Box>
                        </div>
                        
                        <div>
                          <Box variant="h5">Performance nach Verkauf</Box>
                          <Box 
                            variant="awsui-value-large" 
                            color={
                              portfolioStatistikenNachVerkauf.performanceNachVerkauf > 0 ? "text-status-success" : 
                              portfolioStatistikenNachVerkauf.performanceNachVerkauf < 0 ? "text-status-error" : 
                              "text-body-secondary"
                            }
                          >
                            {portfolioStatistikenNachVerkauf.performanceNachVerkauf >= 0 ? '+' : ''}{portfolioStatistikenNachVerkauf.performanceNachVerkauf.toFixed(2)}%
                          </Box>
                          <Box 
                            variant="small" 
                            color={
                              portfolioStatistikenNachVerkauf.absoluteGainNachVerkauf > 0 ? "text-status-success" : 
                              portfolioStatistikenNachVerkauf.absoluteGainNachVerkauf < 0 ? "text-status-error" : 
                              "text-body-secondary"
                            }
                          >
                            {portfolioStatistikenNachVerkauf.absoluteGainNachVerkauf >= 0 ? '+' : ''}{formatCurrency(portfolioStatistikenNachVerkauf.absoluteGainNachVerkauf)} Gewinn/Verlust
                          </Box>
                        </div>

                        <div>
                          <Box variant="h5">Positionen nach Verkauf</Box>
                          <Box variant="awsui-value-large" color="text-status-info">
                            {portfolioStatistikenNachVerkauf.anzahlPositionenNachVerkauf}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Verbleibende Positionen
                          </Box>
                        </div>
                      </SpaceBetween>
                    </div>

                    {/* Verteilung nach Verkauf Chart */}
                    <div>
                      {afterSalePortfolioChartData && afterSalePortfolioChartData.length > 0 ? (
                        <PieChart
                          data={afterSalePortfolioChartData}
                          size="large"
                          detailPopoverContent={(datum, sum) => [
                            { key: 'Asset', value: datum.originalName || 'Unbekannt' },
                            { key: 'Anteil', value: `${((datum.value / sum) * 100).toFixed(2)}%` },
                            { key: 'Wert', value: formatCurrency(datum.datum) }
                          ]}
                          segmentDescription={(datum, sum) =>
                            `${formatCurrency(datum.datum)}, ${((datum.value / sum) * 100).toFixed(1)}%`
                          }
                          ariaDescription="Kreisdiagramm zeigt die Portfolio-Verteilung nach geplanten Verkäufen."
                          ariaLabel="Portfolio-Verteilung nach Verkauf"
                          fitHeight
                          hideDescriptions
                          hideFilter
                          hideLegend
                          hideTitles
                          empty={
                            <Box textAlign="center" color="inherit">
                              <b>Keine Portfolio-Daten</b>
                              <Box variant="p" color="inherit">
                                Es sind keine Verkäufe geplant
                              </Box>
                            </Box>
                          }
                          noMatch={
                            <Box textAlign="center" color="inherit">
                              <b>Keine passenden Daten</b>
                              <Box variant="p" color="inherit">
                                Es gibt keine passenden Daten anzuzeigen
                              </Box>
                            </Box>
                          }
                        />
                      ) : (
                        <Box textAlign="center" padding="l">
                          <Box variant="p" color="text-body-secondary">
                            Keine Verkäufe geplant oder Portfolio vollständig verkauft
                          </Box>
                        </Box>
                      )}
                      <Box variant="small" color="text-body-secondary" margin={{ top: 'xs' }}>
                        Segmente nach ISIN aggregiert (nicht nach Depot).
                      </Box>
                    </div>
                  </ColumnLayout>
                </SpaceBetween>
            </Container>
          </ColumnLayout>
        </Container>

      {/* Portfolio-Entnahme */}
      <Container
        header={
            <Header 
              variant="h3"
              description="Detaillierte Übersicht Ihrer geplanten Portfolio-Entnahme"
            >
            Portfolio-Entnahme
          </Header>
        }
      >
                        <SpaceBetween direction="vertical" size="xs">
              {/* Moved fields from Ziel-Entnahmebetrag container */}
              <ColumnLayout columns={2}>
                <FormField label="Gewünschter Verkaufserlös in €">
                  <Input
                    value={zielEntnahmebetrag}
                    onChange={({ detail }) => handleZielEntnahmebetragChange(detail.value)}
                    type="number"
                    inputMode="numeric"
                    step={0.01}
                    placeholder="0"
                  />
                </FormField>
                
                <FormField 
                  label={`Portfolio-Entnahme: ${portfolioPercentage.toFixed(1)}% (${formatCurrency(portfolioStatistiken.gesamtCurrentValue * portfolioPercentage / 100)})`}
                  description="Beispielhafte Entnahmerateauswahl zwischen 0% und 10% des Portfoliowerts (nur zu Illustrationszwecken)"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Slider
                      value={portfolioPercentage} // portfolioPercentage is already aligned via useMemo
                      onChange={({ detail }) => {
                        const alignedValue = alignToStep(detail.value);
                        setPortfolioPercentageRaw(alignedValue); // Use aligned value immediately
                        
                        // Calculate and update target amount based on aligned percentage
                        if (portfolioStatistiken.gesamtCurrentValue > 0) {
                          const newAmount = (portfolioStatistiken.gesamtCurrentValue * alignedValue / 100);
                          handleZielEntnahmebetragChange(Math.round(newAmount).toString());
                        }
                      }}
                      min={0}
                      max={10}
                      step={0.1}
                      hideFillLine={false}
                      tickMarks={true}
                      disabled={portfolioStatistiken.gesamtCurrentValue <= 0}
                    />
                    <Box textAlign="center" color="text-body-secondary">
                      <Box variant="small">
                        In Literaturquellen genannter Orientierungsbereich für nachhaltigen Vermögenserhalt: 2% - 6% (nur zur allgemeinen Veranschaulichung – keine Empfehlung)
                      </Box>
                    </Box>
                  </div>
                </FormField>
              </ColumnLayout>
              
              {/* Sparer-Pauschbetrag Einstellungen */}
              <Checkbox
                onChange={({ detail }) => setSparerPauschbetragEnabled(detail.checked)}
                checked={sparerPauschbetragEnabled}
                description="Zieht Ihren jährlichen Sparer-Pauschbetrag von den Steuern ab"
              >
                Sparer-Pauschbetrag berücksichtigen ({formatCurrency(sparerPauschbetragBetrag)})
              </Checkbox>

              {/* Compact KPI Grid - dynamic columns based on Pauschbetrag usage */}
              {entnahmeSimulation && (
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: `repeat(${sparerPauschbetragEnabled && steuerStatistiken.gesamtVerwendeterPauschbetrag > 0 ? 6 : 5}, 1fr)`, 
                    gap: '12px' 
                  }}>
                {/* Ziel-Entnahmebetrag */}
                <div style={{ 
                  padding: '16px', 
                  border: '1px solid #d5dbdb', 
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc'
                }}>
                  <Box variant="awsui-key-label">Ziel-Entnahmebetrag</Box>
                  <Box variant="awsui-value-large" color="text-status-info">
                    {formatCurrency(entnahmeSimulation.zielEntnahmebetrag)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Gewünschter Betrag
                  </Box>
                </div>

                {/* Brutto-Verkaufserlös */}
                <div style={{ 
                  padding: '16px', 
                  border: '1px solid #d5dbdb', 
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc'
                }}>
                  <Box variant="awsui-key-label">Brutto-Verkaufserlös</Box>
                  <Box variant="awsui-value-large">
                    {formatCurrency(entnahmeSimulation.tatsaechlicherVerkaufserloes)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Vor Steuern
                  </Box>
                  <div style={{ marginTop: '4px' }}>
                    <Box variant="small" color={entnahmeSimulation.zielerreichung >= 95 ? "text-status-success" : "text-status-warning"}>
                      <strong>Zielerreichung: {entnahmeSimulation.zielerreichung.toFixed(1)}%</strong>
                    </Box>
                  </div>
                  {entnahmeSimulation.zielerreichung < 95 && (
                    <div style={{ fontSize: '11px' }}>
                      <Box variant="small" color="text-status-info">
                        Fehlbetrag: {formatCurrency(
                          entnahmeSimulation.tatsaechlicherVerkaufserloes - 
                          entnahmeSimulation.zielEntnahmebetrag
                        )}
                      </Box>
                    </div>
                  )}
                  {entnahmeSimulation.zielEntnahmebetrag > portfolioStatistiken.gesamtCurrentValue && (
                    <div style={{ fontSize: '10px', marginTop: '2px' }}>
                      <Box variant="small" color="text-status-warning">
                        ⚠️ Ziel übersteigt Portfolio-Wert
                      </Box>
                    </div>
                  )}
                </div>

                {/* Steuerpflichtiger Gewinn */}
                <div style={{ 
                  padding: '16px', 
                  border: '1px solid #d5dbdb', 
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc'
                }}>
                  <Box variant="awsui-key-label">Steuerpflichtiger Gewinn</Box>
                  <Box variant="awsui-value-large" color="text-status-warning">
                    {formatCurrency(
                      lastOptimizationResults
                        ? (lastOptimizationResults.totalSteuerpflichtigerGewinnNachPausch || 0)
                        : steuerStatistiken.gesamtSteuerpflichtigerGewinn
                    )}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    {lastOptimizationResults
                      ? 'Nach positionsübergreifender Verrechnung und Sparer-Pauschbetrag (wie Tabellensteuer); FIFO vor Pausch siehe Simulationsexport'
                      : 'Nach FIFO + Teilfreistellungen'}
                  </Box>
                </div>

                {/* Sparer-Pauschbetrag (nur wenn aktiviert und verwendet) */}
                {sparerPauschbetragEnabled && steuerStatistiken.gesamtVerwendeterPauschbetrag > 0 && (
                  <div style={{ 
                    padding: '16px', 
                    border: '1px solid #d5dbdb', 
                    borderRadius: '8px',
                    backgroundColor: '#fafbfc'
                  }}>
                    <Box variant="awsui-key-label">Sparer-Pauschbetrag</Box>
                    <Box variant="awsui-value-large" color="text-status-success">
                      -{formatCurrency(steuerStatistiken.gesamtVerwendeterPauschbetrag)}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      von {formatCurrency(sparerPauschbetragBetrag)} verwendet
                    </Box>
                  </div>
                )}

                {/* Gesamte Steuerlast */}
                <div style={{ 
                  padding: '16px', 
                  border: '1px solid #d5dbdb', 
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc'
                }}>
                  <Box variant="awsui-key-label">Gesamte Steuerlast</Box>
                  <Box variant="awsui-value-large" color="text-status-error">
                    {formatCurrency(steuerStatistiken.gesamtSteuer)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    {(() => {
                      // Show detailed breakdown based on church tax setting
                      if (steuerParameter?.kirchensteuer === 'baden-wuerttemberg-bayern') {
                        return 'KapESt (24.51%) + SolZ (1.35%) + KiSt (1.96%)';
                      } else if (steuerParameter?.kirchensteuer === 'andere-bundeslaender') {
                        return 'KapESt (24.45%) + SolZ (1.34%) + KiSt (2.20%)';
                      } else {
                        return 'KapESt (25.00%) + SolZ (1.38%) + KiSt (0.00%)';
                      }
                    })()}
                  </Box>
                </div>

                {/* Netto-Erlös */}
                <div style={{ 
                  padding: '16px', 
                  border: '1px solid #d5dbdb', 
                  borderRadius: '8px',
                  backgroundColor: '#fafbfc'
                }}>
                  <Box variant="awsui-key-label">Netto-Erlös</Box>
                  <Box variant="awsui-value-large" color="text-status-success">
                    {formatCurrency(entnahmeSimulation.tatsaechlicherVerkaufserloes - steuerStatistiken.gesamtSteuer)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Nach Steuern
                  </Box>
                </div>
              </div>


                </>
              )}

              {!entnahmeSimulation && (
                <Alert type="info">
                  <strong>Simulation bereit</strong>
                  <br />
                  Geben Sie einen Ziel-Entnahmebetrag ein oder setzen Sie Verkaufsanteile für Portfolio-Positionen, um die Simulation zu starten.
                </Alert>
              )}
            </SpaceBetween>
          </Container>
      </SpaceBetween>

      {/* Portfolio-Tabelle */}
      <Table
        columnDefinitions={columnDefinitions}
        items={sortedPortfolioPositionen}
        loadingText="Lade Portfolio-Positionen..."
        sortingColumn={{ sortingField: sortingColumn }}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => handleSortingChange(detail)}
        empty={
          <Box textAlign="center" color="inherit">
            <b>Keine Portfolio-Positionen gefunden</b>
            <Box variant="p" color="text-body-secondary">
              Importieren Sie Transaktionsdaten, um Portfolio-Positionen anzuzeigen.
            </Box>
          </Box>
        }
        header={
          <Header
            counter={`${sortedPortfolioPositionen.length} Position${sortedPortfolioPositionen.length !== 1 ? 'en' : ''}`}
            description="Portfolio-Positionen mit aktueller Performance und editierbaren Verkauf %-Eingaben"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button 
                  variant="normal"
                  onClick={handleVerkaufLeeren}
                >
                  Verkauf % leeren
                </Button>
                <Button 
                  variant="normal"
                  onClick={handleSymmetrischerVerkauf}
                >
                  Symmetrischer Verkauf
                </Button>
                <Button 
                  variant="normal"
                  onClick={handleSteuerOptimiererVerkauf}
                >
                  Simulation einer möglichen Verkaufsreihenfolge
                </Button>
                {lastOptimizationResults && (
                  <Button 
                    variant="normal"
                    onClick={() => setShowOptimizationDetails(!showOptimizationDetails)}
                  >
                    {showOptimizationDetails ? 'Details zur Berechnungslogik unter Tabelle ausblenden' : 'Details zur Berechnungslogik unter Tabelle anzeigen'}
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            Portfolio-Positionen
          </Header>
        }
        ariaLabels={{
          allItemsSelectionLabel: () => "Alle Portfolio-Positionen auswählen",
          itemSelectionLabel: (_selection, item) => `Portfolio-Position ${item.name} auswählen`,
          selectionGroupLabel: "Portfolio-Positionen Auswahl",
          resizerRoleDescription: "Spaltenbreite ändern",
          tableLabel: "Portfolio-Positionen Tabelle",
          activateEditLabel: (column, item) => `${column.header} für ${item.name} bearbeiten`,
          cancelEditLabel: (column) => `${column.header} Bearbeitung abbrechen`,
          submitEditLabel: (column) => `${column.header} Änderungen speichern`,
        }}
        resizableColumns={true}
        stickyHeader={true}
        stripedRows={true}
        trackBy={(item) => `${item.identifier}|${item.portfolio || 'Unknown'}`}
        variant="container"
        wrapLines={false}
      />

      {/* Tax Optimization Details Section */}
      {showOptimizationDetails && lastOptimizationResults && (
        <Container
          header={
            <Header
              variant="h3"
              description="Simulationsergebnisse für verkaufsbasierte Steuerbelastung"
            >
              Simulation einer möglichen Verkaufsreihenfolge
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            {/* Summary Statistics removed as redundant - information already shown in Portfolio-Entnahme section */}

            {/* Export Button */}
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <Button 
                variant="primary" 
                iconName="download"
                onClick={() => {
                  const exportData = {
                    optimizationResults: {
                      timestamp: lastOptimizationResults.timestamp,
                      targetAmount: lastOptimizationResults.targetAmount,
                      achievedAmount: lastOptimizationResults.achievedAmount,
                      totalTaxAmount: lastOptimizationResults.totalTaxAmount,
                      totalTaxableProfit: lastOptimizationResults.totalTaxableProfit,
                      totalFifoTaxableProfitBeforeAllowance:
                        lastOptimizationResults.totalFifoTaxableProfitBeforeAllowance,
                      totalSteuerpflichtigerGewinnNachPausch:
                        lastOptimizationResults.totalSteuerpflichtigerGewinnNachPausch,
                      taxEfficiency: lastOptimizationResults.taxEfficiency,
                      usedSparerPauschbetrag: lastOptimizationResults.usedSparerPauschbetrag
                    },
                    sellInstructions: lastOptimizationResults.sellInstructions.map((instr: OptimizationSellInstruction) => ({
                      positionKey: instr.positionKey ?? portfolioPositionKey(instr.position),
                      positionName: instr.position.name,
                      sellPercentage: instr.sellPercentage,
                      totalSaleValue: instr.totalSaleValue,
                      totalTaxAmount: instr.totalTaxAmount,
                      totalTaxableProfit: instr.totalTaxableProfit,
                      effectiveTaxableProfit: instr.effectiveTaxableProfit,
                      usedSparerPauschbetrag: instr.usedSparerPauschbetrag,
                      taxTreatment: instr.position.taxTreatment,
                      fifoNote: "Position sold using FIFO principle (oldest lots first)"
                    })),
                    detailedLotAnalysis: lastOptimizationResults.detailedAnalysis.map((analysis: any) => ({
                      positionName: analysis.positionName,
                      taxTreatment: analysis.taxTreatment,
                      isSold: analysis.isSold,
                      lots: analysis.lots.map((lot: any) => ({
                        lotIndex: lot.lotIndex,
                        portfolio: lot.portfolio,
                        quantity: lot.quantity,
                        purchasePrice: lot.purchasePrice,
                        currentValue: lot.currentValue,
                        holdingPeriodYears: lot.holdingPeriodYears,
                        profit: lot.profit,
                        taxableProfit: lot.taxableProfit,
                        taxAmount: lot.taxAmount,
                        taxPerEuro: lot.taxPerEuro,
                        isTaxFree: lot.isTaxFree,
                        isLoss: lot.isLoss,
                        effectiveTaxTreatment: lot.effectiveTaxTreatment,
                        priority: lot.priority
                      }))
                    })),
                    algorithm: {
                      strategy: lastOptimizationResults.strategy || "FIFO-konforme Berechnungsvariante auf Positionsebene mit Sparer-Pauschbetrag",
                      method: "Position-level selection with FIFO compliance within each position",
                      fifoCompliance: "Full FIFO compliance: oldest lots sold first within each position, optimizes between positions",
                      note: "Algorithm prioritizes positions by tax efficiency while respecting FIFO law: 1) Positions with losses (harvest losses), 2) Positions with tax-free gains, 3) Positions benefiting from Sparer-Pauschbetrag, 4) Lowest-tax positions"
                    },
                    exportMetadata: {
                      exportedAt: new Date().toISOString(),
                      portfolioWizardVersion: "1.5.1",
                      purpose: "FIFO calculation analysis for simulation purposes"
                    }
                  };
                  
                  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `portfolio-simulation-${new Date().toISOString().split('T')[0]}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                Simulation einer möglichen Verkaufsreihenfolge als JSON exportieren
              </Button>
            </div>



            {/* Detailed Lot Analysis */}
            <Box variant="h4" margin={{ bottom: 's' }}>📊 Detaillierte Lot-Analyse (Alle Assets)</Box>
            <Alert type="info">
              <strong>Diese Tabelle zeigt ALLE Assets mit ALLEN Lots und deren Bewertung:</strong>
              <br />

              <div style={{ marginTop: '8px', fontSize: '13px' }}>
                • <strong>→ Orange markierte Lots:</strong> Im Simulationsszenario ausgewählte Lots<br />
                • <strong>Lot:</strong> Portfolio-spezifische Lot-Nummer (z.B. TRA-1, SCA-2)<br />
                • <strong>Portfolio:</strong> Bei welchem Portfolio dieses Lot gekauft wurde<br />
                • <strong>Anteile:</strong> Anzahl der gekauften Wertpapier-Anteile in diesem Lot<br />
                • <strong>Kaufpreis:</strong> Ursprünglicher Gesamtpreis für dieses Lot<br />
                • <strong>Gesamtwert:</strong> Aktueller Marktwert dieses Lots<br />
                • <strong>Steuerpl. Gewinn:</strong> Gewinn nach Teilfreistellungen (das wird besteuert)<br />
                • <strong>Steuerbetrag:</strong> Gesamte Steuer für dieses Lot<br />
                • <strong>Steuerpl. Gewinn/Anteil:</strong> Steuerpflichtiger Gewinn pro einzelnem Anteil<br />
                • <strong>Steuerbetrag/Anteil:</strong> Steuerbetrag pro einzelnem Anteil<br />
                • <strong>Berechnungsreihenfolge:</strong> Niedrigere Zahl = frühere Berechnung im Modell<br />
                • <strong>Grund:</strong> Warum dieses Lot diese Reihenfolge hat
              </div>
            </Alert>
            
            {lastOptimizationResults.detailedAnalysis &&
              lastOptimizationResults.detailedAnalysis.map((posAnalysis: OptimizationPositionAnalysis) => {
                const optimizationPosKey = posAnalysis.positionKey;
                const isLotMarkedSold = (lotIndex: number) =>
                  lastOptimizationResults.sellInstructions.some(
                    (instruction) =>
                      instruction.positionKey === optimizationPosKey &&
                      instruction.lotsToSell.some((soldLot) => soldLot.lotIndex === lotIndex)
                  );

                return (
              <Container key={posAnalysis.positionKey}>
                <Box variant="h5" margin={{ bottom: 's' }}>
                  {posAnalysis.isSold && <span style={{ color: '#d97706', marginRight: '8px' }}>→</span>}
                  {posAnalysis.positionName}{' '}
                  <Box display="inline" color="text-body-secondary" fontSize="body-s">
                    ({posAnalysis.identifier})&nbsp;· Steuerbehandlung: {posAnalysis.taxTreatment}
                    {posAnalysis.isSold && <span style={{ color: '#d97706', marginLeft: '8px', fontWeight: 'bold' }}>VERKAUFT</span>}
                  </Box>
                </Box>
                
                <Table
                  columnDefinitions={[
                    {
                      id: 'lot',
                      header: 'Lot',
                      cell: (item: OptimizationLotAnalysisRow) => {
                        const portfolioPrefix =
                          item.portfolio === 'Unknown'
                            ? 'UNK'
                            : item.portfolio?.substring(0, 3).toUpperCase() || 'UNK';

                        const positionLots = posAnalysis.lots || [];
                        const samePortfolioLots = positionLots.filter(
                          (lot: OptimizationLotAnalysisRow) => lot.portfolio === item.portfolio
                        );
                        const portfolioLotIndex =
                          samePortfolioLots.findIndex((lot) => lot.lotIndex === item.lotIndex) + 1;

                        const isSold = isLotMarkedSold(item.lotIndex);

                        return (
                          <Box fontWeight={isSold ? "bold" : "normal"} color={isSold ? "text-status-warning" : "inherit"}>
                            {isSold && "→ "}{`${portfolioPrefix}-${portfolioLotIndex}`}
                          </Box>
                        );
                      },
                      sortingField: 'lotIndex'
                    },
                    {
                      id: 'portfolio',
                      header: 'Portfolio',
                      cell: (item: OptimizationLotAnalysisRow) => {
                        const isSold = isLotMarkedSold(item.lotIndex);
                        return (
                          <Box fontWeight={isSold ? "bold" : "normal"} color={isSold ? "text-status-warning" : "inherit"}>
                            {item.portfolio || 'Unknown'}
                          </Box>
                        );
                      },
                      sortingField: 'portfolio'
                    },
                    {
                      id: 'quantity',
                      header: 'Anteile',
                      cell: (item: OptimizationLotAnalysisRow) => {
                        const isSold = isLotMarkedSold(item.lotIndex);
                        return (
                          <Box fontWeight={isSold ? "bold" : "normal"} color={isSold ? "text-status-warning" : "inherit"}>
                            {item.quantity?.toFixed(4) || '0.0000'}
                          </Box>
                        );
                      }
                    },
                    {
                      id: 'purchasePrice',
                      header: 'Kaufpreis',
                      cell: (item: any) => formatCurrency(item.purchasePrice || 0)
                    },
                    {
                      id: 'currentValue',
                      header: 'Gesamtwert',
                      cell: (item: any) => formatCurrency(item.currentValue || 0)
                    },
                    {
                      id: 'holdingPeriod',
                      header: 'Haltefrist',
                      cell: (item: any) => `${item.holdingPeriodYears.toFixed(1)} Jahre`
                    },
                    {
                      id: 'profit',
                      header: 'Gewinn',
                      cell: (item: any) => formatCurrency(item.profit)
                    },
                    {
                      id: 'taxableProfit',
                      header: 'Steuerpl. Gewinn',
                      cell: (item: any) => (
                        <Box color={item.taxableProfit < 0 ? "text-status-error" : 
                                   item.isTaxFree ? "text-status-success" : 
                                   "inherit"}>
                          {formatCurrency(item.taxableProfit)}
                        </Box>
                      )
                    },
                    {
                      id: 'taxAmount',
                      header: 'Steuerbetrag',
                      cell: (item: any) => (
                        <Box color="text-status-error">
                          {formatCurrency(item.taxAmount)}
                        </Box>
                      )
                    },
                    {
                      id: 'taxableGainPerAsset',
                      header: 'Steuerpl. Gewinn/Anteil',
                      cell: (item: any) => {
                        const gainPerAsset = item.quantity > 0 ? item.taxableProfit / item.quantity : 0;
                        return (
                          <Box color={gainPerAsset < 0 ? "text-status-error" : 
                                     item.isTaxFree ? "text-status-success" : 
                                     "text-status-warning"}>
                            {formatCurrency(gainPerAsset)}
                          </Box>
                        );
                      }
                    },
                    {
                      id: 'taxAmountPerAsset',
                      header: 'Steuerbetrag/Anteil',
                      cell: (item: any) => {
                        const taxPerAsset = item.quantity > 0 ? item.taxAmount / item.quantity : 0;
                        return (
                          <Box color="text-status-error">
                            {formatCurrency(taxPerAsset)}
                          </Box>
                        );
                      }
                    },
                    {
                      id: 'priority',
                      header: 'Reihenfolge',
                      cell: (item: any) => (
                        <Box fontWeight="bold">
                          {item.priority.toFixed(0)}
                        </Box>
                      )
                    },
                    {
                      id: 'reason',
                      header: 'Grund',
                      cell: (item: any) => {
                        const reason = item.isLoss ? 'Verlust (Verluste werden im Berechnungsmodell rechnerisch berücksichtigt)' : 
                                     item.isTaxFree ? 'Steuerfrei (>1 Jahr Haltefrist)' : 
                                     item.effectiveTaxTreatment.includes('30%') ? 'Teilfreistellung 30%' :
                                     item.effectiveTaxTreatment.includes('15%') ? 'Teilfreistellung 15%' :
                                     'Regulär besteuert';
                        return (
                          <Box fontSize="body-s">
                            {reason}
                          </Box>
                        );
                      }
                    }
                  ]}
                  items={posAnalysis.lots.sort((a: any, b: any) => a.priority - b.priority)}
                  loadingText="Lade Lot-Analyse..."
                  empty="Keine Lots verfügbar"
                  variant="embedded"
                />
              </Container>
                );
              })}

            {/* Verkaufsstrategie Summary */}
            <Box variant="h4" margin={{ bottom: 's', top: 'l' }}>Zusammenfassung der Simulationsparameter</Box>
            <Table
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Wertpapier',
                  cell: (item: any) => item.position.name,
                  sortingField: 'position.name'
                },
                {
                  id: 'taxTreatment',
                  header: 'Steuerbehandlung',
                  cell: (item: any) => (
                    <Box fontSize="body-s">
                      {item.position.taxTreatment || 'keine Teilfreistellung'}
                    </Box>
                  )
                },
                {
                  id: 'percentage',
                  header: 'Verkaufsanteil',
                  cell: (item: any) => `${item.sellPercentage.toFixed(2)}%`
                },
                {
                  id: 'lots',
                  header: 'Verkaufte Lots (FIFO-Reihenfolge)',
                  cell: (item: any) => {
                    // Check if we have lot-level data with quantities
                    if (item.lotsToSell && Array.isArray(item.lotsToSell) && item.lotsToSell.length > 0) {
                      const lotDetails = item.lotsToSell.map((lot: any) => {
                        const lotNumber = lot.lotIndex + 1;
                        const quantity = lot.quantity?.toFixed(4) || '0.0000';
                        const isPartial = lot.partialSale ? ' (teilweise)' : '';
                        return `Lot ${lotNumber}: ${quantity} Anteile${isPartial}`;
                      });
                      
                      const totalSoldQuantity = item.lotsToSell.reduce((sum: number, lot: any) => sum + (lot.quantity || 0), 0);
                      
                      return (
                        <Box fontSize="body-s">
                          <div><strong>{item.lotsToSell.length}</strong> Lot(s) - <strong>{totalSoldQuantity.toFixed(4)} Anteile gesamt</strong></div>
                          <div style={{ color: '#16a34a', fontSize: '11px', marginTop: '4px' }}>
                            {lotDetails.map((detail: any, index: number) => (
                              <div key={index}>→ {detail}</div>
                            ))}
                          </div>
                        </Box>
                      );
                    }
                    
                    // For position-level optimization, calculate lots from position data
                    if (item.position && item.position.purchaseLots && item.sellPercentage > 0 && !item.position.exclude) {
                      const totalQuantity = item.position.quantity;
                      const sellQuantity = totalQuantity * (item.sellPercentage / 100);
                      let remainingSellQuantity = sellQuantity;
                      let lotsUsed = 0;
                      const lotDetails: string[] = [];
                      
                      // FIFO: simulate which lots would be used and calculate quantities
                      const sortedLots = item.position.purchaseLots
                        .map((lot: any, index: number) => ({ ...lot, originalIndex: index }))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                      
                      for (const lot of sortedLots) {
                        if (remainingSellQuantity <= 0) break;
                        lotsUsed++;
                        
                        const lotQuantityToSell = Math.min(lot.quantity, remainingSellQuantity);
                        const isPartial = lotQuantityToSell < lot.quantity;
                        const partialText = isPartial ? ' (teilweise)' : '';
                        
                        lotDetails.push(`Lot ${lot.originalIndex + 1}: ${lotQuantityToSell.toFixed(4)} Anteile${partialText}`);
                        remainingSellQuantity -= lotQuantityToSell;
                      }
                      
                      return (
                        <Box fontSize="body-s">
                          <div><strong>{lotsUsed}</strong> Lot(s) - <strong>{sellQuantity.toFixed(4)} Anteile gesamt</strong></div>
                          <div style={{ color: '#16a34a', fontSize: '11px', marginTop: '4px' }}>
                            {lotDetails.map((detail: any, index: number) => (
                              <div key={index}>→ {detail}</div>
                            ))}
                          </div>
                        </Box>
                      );
                    }
                    
                    return (
                      <Box fontSize="body-s">
                        <div style={{ color: '#6b7280' }}>-</div>
                      </Box>
                    );
                  }
                },
                {
                  id: 'priorityScore',
                  header: 'Priorität',
                  cell: (item: any) => {
                    // Check if we have priority from position-level optimization
                    if (item.priority !== undefined) {
                      return (
                        <Box fontSize="body-s">
                          <div><strong>{item.priority.toFixed(0)}</strong></div>
                          <div style={{ color: '#6b7280', fontSize: '11px' }}>
                            Position-Level
                          </div>
                        </Box>
                      );
                    }
                    
                    // Find corresponding position analysis to get lot priorities
                    const instrPosKey = portfolioPositionKey(item.position);
                    const posAnalysis = lastOptimizationResults.detailedAnalysis?.find(
                      (analysis) => analysis.positionKey === instrPosKey
                    );

                    if (posAnalysis && posAnalysis.lots && item.lotsToSell && Array.isArray(item.lotsToSell)) {
                      const soldLotIndices = item.lotsToSell.map(
                        (lot: { lotIndex: number }) => lot.lotIndex
                      );
                      const soldLots = posAnalysis.lots.filter((lot) =>
                        soldLotIndices.includes(lot.lotIndex)
                      );

                      if (soldLots.length > 0) {
                        const avgPriority =
                          soldLots.reduce((sum, lot) => sum + lot.priority, 0) / soldLots.length;
                        const lowestPriority = Math.min(...soldLots.map((lot) => lot.priority));
                        
                        return (
                          <Box fontSize="body-s">
                            <div><strong>{avgPriority.toFixed(0)}</strong></div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>
                              Min: {lowestPriority.toFixed(0)}
                            </div>
                          </Box>
                        );
                      }
                    }
                    
                    return <Box fontSize="body-s">-</Box>;
                  }
                },
                {
                  id: 'lotInfo',
                  header: 'Lot Info',
                  cell: (item: any) => {
                    if (item.position && item.position.purchaseLots && item.sellPercentage > 0 && !item.position.exclude) {
                      const totalQuantity = item.position.quantity;
                      const sellQuantity = totalQuantity * (item.sellPercentage / 100);
                      let remainingSellQuantity = sellQuantity;
                      const sortedLots = item.position.purchaseLots
                        .map((lot: any) => ({ ...lot }))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                      const consumed: { date: string; quantity: number }[] = [];
                      for (const lot of sortedLots) {
                        if (remainingSellQuantity <= 0) break;
                        const qty = Math.min(lot.quantity, remainingSellQuantity);
                        consumed.push({ date: lot.date, quantity: qty });
                        remainingSellQuantity -= qty;
                      }
                      
                      if (consumed.length > 0) {
                        const totalConsumedQty = consumed.reduce((s, c) => s + c.quantity, 0);
                        const avgDate =
                          consumed.reduce((s, c) => s + new Date(c.date).getTime() * c.quantity, 0) /
                          totalConsumedQty;
                        const avgAge = (Date.now() - avgDate) / (1000 * 60 * 60 * 24); // days
                        
                        return (
                          <Box fontSize="body-s">
                            <div><strong>FIFO</strong></div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>
                              Ø {Math.round(avgAge)}d alt
                            </div>
                          </Box>
                        );
                      }
                    }
                    
                    return <Box fontSize="body-s">-</Box>;
                  }
                },
                {
                  id: 'selectionReason',
                  header: 'Auswahlgrund',
                  cell: (item: any) => {
                    // Find the primary reason for selection based on tax treatment and lots
                    const selInstrPosKey = portfolioPositionKey(item.position);
                    const posAnalysis = lastOptimizationResults.detailedAnalysis?.find(
                      (analysis) => analysis.positionKey === selInstrPosKey
                    );

                    if (posAnalysis && posAnalysis.lots && item.lotsToSell && Array.isArray(item.lotsToSell)) {
                      const soldLotIndices = item.lotsToSell.map(
                        (lot: { lotIndex: number }) => lot.lotIndex
                      );
                      const soldLots = posAnalysis.lots.filter((lot) =>
                        soldLotIndices.includes(lot.lotIndex)
                      );

                      if (soldLots.length > 0) {
                        const hasLosses = soldLots.some((lot) => lot.isLoss);
                        const hasTaxFree = soldLots.some((lot) => lot.isTaxFree);
                        const hasPartialExemption = soldLots.some(
                          (lot) =>
                            lot.effectiveTaxTreatment?.includes('15%') ||
                            lot.effectiveTaxTreatment?.includes('30%')
                        );
                        
                        let reason = '';
                        let icon = '';
                        
                        if (hasLosses) {
                          reason = 'Verluste werden im Berechnungsmodell rechnerisch berücksichtigt (nur Modellannahme)';
                          icon = '−';
                        } else if (hasTaxFree) {
                          reason = 'Steuerfreie Gewinne';
                          icon = '○';
                        } else if (hasPartialExemption) {
                          reason = 'Teilfreistellung';
                          icon = '◐';
                        } else {
                          reason = 'Niedrigster steuerpl. Gewinn';
                          icon = '•';
                        }
                        
                        return (
                          <Box fontSize="body-s">
                            <div>{icon} <strong>{reason}</strong></div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>
                              FIFO-konform
                            </div>
                          </Box>
                        );
                      } else {
                        // No lots sold data, use position-level tax treatment
                        const taxTreatment = item.position.taxTreatment || 'keine Teilfreistellung';
                        let reason = '';
                        let icon = '';
                        
                        if (taxTreatment.includes('30%')) {
                          reason = 'Teilfreistellung 30%';
                          icon = '◐';
                        } else if (taxTreatment.includes('15%')) {
                          reason = 'Teilfreistellung 15%';
                          icon = '◐';
                        } else if (taxTreatment.includes('Steuerfrei')) {
                          reason = 'Steuerfreie Position';
                          icon = '○';
                        } else {
                          reason = 'Beispielszenario, das im Modell die niedrigste Steuerlast zeigt (nur zu Illustrationszwecken)';
                          icon = '•';
                        }
                        
                        return (
                          <Box fontSize="body-s">
                            <div>{icon} <strong>{reason}</strong></div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>
                              Position-Level
                            </div>
                          </Box>
                        );
                      }
                    } else {
                      // No position analysis available, show basic reason
                      const taxTreatment = item.position.taxTreatment || 'keine Teilfreistellung';
                      let reason = '';
                      let icon = '';
                      
                      if (taxTreatment.includes('30%')) {
                        reason = 'Teilfreistellung 30%';
                        icon = '◐';
                      } else if (taxTreatment.includes('15%')) {
                        reason = 'Teilfreistellung 15%';
                        icon = '◐';
                      } else if (taxTreatment.includes('Steuerfrei')) {
                        reason = 'Steuerfreie Position';
                        icon = '○';
                      } else {
                        reason = 'Steueroptimiert';
                        icon = '💰';
                      }
                      
                      return (
                        <Box fontSize="body-s">
                          <div>{icon} <strong>{reason}</strong></div>
                        </Box>
                      );
                    }
                  }
                },
                {
                  id: 'saleValue',
                  header: 'Verkaufserlös',
                  cell: (item: any) => formatCurrency(item.totalSaleValue)
                },
                {
                  id: 'taxableProfit',
                  header: 'Steuerpl. Gewinn (FIFO, v. Pausch)',
                  cell: (item: any) => (
                    <Box color={(item.totalTaxableProfit || 0) < 0 ? "text-status-error" : 
                               (item.totalTaxableProfit || 0) === 0 ? "text-status-success" : 
                               "text-status-warning"}>
                      <strong>{formatCurrency(item.totalTaxableProfit || 0)}</strong>
                    </Box>
                  )
                },
                {
                  id: 'effectiveTaxableProfit',
                  header: 'Anteil steuerpl. Basis n. Pausch',
                  cell: (item: any) => (
                    <Box fontSize="body-s">
                      <strong>{formatCurrency(item.effectiveTaxableProfit ?? 0)}</strong>
                    </Box>
                  )
                },
                {
                  id: 'taxAmount',
                  header: 'Steuerbelastung',
                  cell: (item: any) => (
                    <Box color="text-status-error">
                      <strong>{formatCurrency(item.totalTaxAmount || 0)}</strong>
                    </Box>
                  )
                }
              ]}
              items={lastOptimizationResults.sellInstructions || []}
              loadingText="Lade Verkaufsstrategie..."
              empty="Keine beispielhafte Verkaufsreihenfolge verfügbar"
            />

            {/* Sparer-Pauschbetrag Summary */}
            {sparerPauschbetragEnabled && lastOptimizationResults && lastOptimizationResults.usedSparerPauschbetrag > 0 && (
              <Container
                header={
                  <Header variant="h3">
                    Sparer-Pauschbetrag Anwendung
                  </Header>
                }
              >
                <ColumnLayout columns={3}>
                  <div>
                    <Box variant="small" color="text-body-secondary">Verfügbarer Pauschbetrag</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(sparerPauschbetragBetrag)}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      {steuerParameter?.familienstand === 'verheiratet' ? 'Verheiratet' : 'Alleinstehend'}
                    </Box>
                  </div>
                  
                  <div>
                    <Box variant="small" color="text-body-secondary">Verwendeter Betrag</Box>
                    <Box variant="h3" color="text-status-success">
                      {formatCurrency(lastOptimizationResults.usedSparerPauschbetrag)}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Reduziert steuerpfl. Gewinn
                    </Box>
                  </div>
                  
                  <div>
                    <Box variant="small" color="text-body-secondary">Verbleibender Betrag</Box>
                    <Box variant="h3" color="text-body-secondary">
                      {formatCurrency(sparerPauschbetragBetrag - lastOptimizationResults.usedSparerPauschbetrag)}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Für weitere Gewinne
                    </Box>
                  </div>
                </ColumnLayout>
                
                <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
                  Der Sparer-Pauschbetrag wurde automatisch auf die steuerpflichtigen Gewinne angewendet, 
                  für die Simulation. Die Darstellung erfolgt in der beispielhaften Verkaufsreihenfolge.
                </Box>
              </Container>
            )}

            {/* Tax Optimization Strategy Summary */}
            <Alert type="success">
              <strong>Berechnungslogik: FIFO</strong>
              <br />
              <div style={{ marginTop: '8px', fontSize: '13px' }}>
                <div>In diesem Beispiel-Szenario werden zunächst <strong>Verlustpositionen</strong> berechnet,</div>
                <div>gefolgt von <strong>steuerfreien Gewinnen</strong> (Haltefrist &gt; 1 Jahr),</div>
                <div>dann <strong>Teilfreistellungen</strong> (15%/30%),</div>
                <div>und schließlich <strong>reguläre Gewinne</strong> (nach steuerlichem Gewinn sortiert).</div>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  <strong>Berechnungsreihenfolge im Modell</strong> - nicht als Handlungsempfehlung zu verstehen.
                </div>
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#16a34a' }}>
                ✅ <strong>FIFO-konform:</strong> Verkauft immer die ältesten Lots zuerst innerhalb jeder Position (deutsches Steuerrecht).
              </div>
            </Alert>



            <Box variant="small" color="text-body-secondary" textAlign="center">
              Simulationslauf vom: {lastOptimizationResults.timestamp}
            </Box>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default TabellenAnsichtTab;